<template>
  <div data-slot="enhanced-textarea" class="relative">
    <!-- The textarea text is transparent; the overlay on top of it renders the
         same text with Facebook emoji images at the same positions. -->
    <div class="group relative">
      <textarea
        ref="textarea"
        v-model="text"
        data-slot="enhanced-textarea-input"
        :maxlength="maxlength"
        :placeholder="placeholder"
        class="glass-input block min-h-21 w-full resize-y rounded-md py-2 pr-9 pl-3.5 text-sm leading-5 text-transparent caret-foreground outline-none [scrollbar-width:none] selection:bg-primary/25 selection:text-transparent placeholder:text-muted-foreground focus-visible:ring-1 focus-visible:ring-ring [&::-webkit-scrollbar]:hidden"
        @scroll="syncScroll"
      />

      <div
        ref="overlay"
        class="pointer-events-none absolute inset-0 h-[calc(100%-5px)] overflow-hidden border border-transparent py-2 pr-9 pl-3.5 text-sm leading-5 text-foreground [&_.emoji]:relative [&_.emoji]:-top-[0.15em] [&_.emoji]:inline-block [&_.emoji]:h-[1em] [&_.emoji]:scale-115 [&_.emoji]:object-contain [&_.emoji]:align-middle"
        v-html="textPreview"
      />

      <div
        v-show="scrollbarVisible"
        ref="scrollTrack"
        class="absolute top-1 right-0.5 bottom-3 z-2 w-2.5 cursor-pointer opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100"
        @click="onTrackClick"
      >
        <div
          ref="scrollThumb"
          class="absolute inset-x-px top-0 min-h-7.5 cursor-pointer rounded bg-foreground/25 transition-colors hover:bg-foreground/40 active:bg-foreground/50"
          :style="{
            height: thumbHeight + 'px',
            transform: `translateY(${thumbTop}px)`
          }"
          @mousedown="onThumbMouseDown"
        />
      </div>
    </div>

    <ui-popover :open="showEmojiPicker" @update:open="(value) => showEmojiPicker = value">
      <ui-popover-trigger as-child>
        <ui-button
          variant="ghost"
          size="icon"
          class="absolute right-2 bottom-3 z-1 size-6.5 text-muted-foreground hover:text-foreground"
          :aria-label="tr('Insert emoji')"
        >
          <smile-icon class="size-4.5" />
        </ui-button>
      </ui-popover-trigger>
      <ui-popover-content
        align="end"
        class="w-auto overflow-hidden p-0 [&_.emoji-mart]:h-75 [&_.emoji-mart]:border-0 [&_.emoji-mart]:bg-transparent [&_.emoji-mart]:text-popover-foreground [&_.emoji-mart-anchor]:text-muted-foreground [&_.emoji-mart-anchor-selected]:text-primary [&_.emoji-mart-anchor-bar]:bg-primary [&_.emoji-mart-bar]:border-border [&_h3.emoji-mart-category-label]:my-0 [&_h3.emoji-mart-category-label]:bg-popover [&_h3.emoji-mart-category-label]:py-1.5 [&_h3.emoji-mart-category-label]:text-xs [&_h3.emoji-mart-category-label]:font-semibold"
      >
        <picker
          :data="emojiIndex"
          set="facebook"
          :emoji-size="24"
          :per-line="8"
          :show-search="false"
          :show-preview="false"
          :i18n="emojiPickerTranslations"
          @select="addEmoji"
        />
      </ui-popover-content>
    </ui-popover>
  </div>
</template>

<script>
import { Smile } from '@lucide/vue';
import emojiData from 'emoji-mart-vue-fast/data/facebook.json';
import { EmojiIndex, Picker } from 'emoji-mart-vue-fast/src';
import emojiRegexBuilder from 'emoji-regex';

const emojiRegex = emojiRegexBuilder();

// The preview is rendered with v-html: the typed text must not become markup.
const HTML_ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;' };

// Emoji measurement system
const emojiWidthCache = new Map();
let measureElement = null;
let isMeasurementReady = false;

export default {
  name: 'EnhancedTextarea',
  components: {
    Picker,
    SmileIcon: Smile,
  },
  props: {
    modelValue: {
      type: [ String, null ],
      required: true,
    },
    maxConsecutiveNewlines: {
      type: Number,
      default: 0,
    },
    maxlength: {
      type: [ Number, null ],
      default: null,
    },
    placeholder: {
      type: String,
      default: null,
    },
  },
  data: () => ({
    showEmojiPicker: false,
    text: '',
    textPreview: '',
    // Custom scrollbar state
    scrollbarVisible: false,
    thumbHeight: 0,
    thumbTop: 0,
    isDragging: false,
    dragStartY: 0,
    dragStartScrollTop: 0,
  }),
  computed: {
    emojiIndex() {
      return new EmojiIndex(JSON.parse(JSON.stringify(emojiData)));
    },
    emojiPickerTranslations() {
      return {
        categories: {
          recent: this.tr('Frequently Used'),
          smileys: this.tr('Smileys & Emotion'),
          people: this.tr('People & Body'),
          nature: this.tr('Animals & Nature'),
          foods: this.tr('Food & Drink'),
          activity: this.tr('Activities'),
          places: this.tr('Travel & Places'),
          objects: this.tr('Objects'),
          symbols: this.tr('Symbols'),
          flags: this.tr('Flags'),
        },
      };
    },
  },
  watch: {
    text(newValue) {
      this.$emit('update:modelValue', newValue);
    },
    modelValue: {
      immediate: true,
      handler(newValue) {
        this.text = newValue || '';
        this.$nextTick(() => this.updatePreview());
      },
    },
  },
  mounted() {
    this.initMeasurement();
    this.updatePreview();

    // ResizeObserver - update preview when textarea is resized (affects line breaks)
    if (this.$refs.textarea) {
      this.resizeObserver = new ResizeObserver(() => {
        this.updatePreview();
      });
      this.resizeObserver.observe(this.$refs.textarea);

      // Prevent too many consecutive newlines
      if (this.maxConsecutiveNewlines > 0) {
        this.setupNewlineRestriction();
      }
    }
  },
  beforeUnmount() {
    document.removeEventListener('mousemove', this.onThumbMouseMove);
    document.removeEventListener('mouseup', this.onThumbMouseUp);
    this.destroyMeasurement();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  },
  methods: {
    // Emoji Measurement System
    initMeasurement() {
      if (!this.$refs.textarea) {
        return;
      }

      document.fonts.ready
        .then(() => {
          if (!this.$refs.textarea) {
            return;
          }

          const style = getComputedStyle(this.$refs.textarea);

          // Create hidden measurement element for emoji width
          measureElement = document.createElement('span');
          measureElement.style.cssText = `
            position: absolute;
            visibility: hidden;
            pointer-events: none;
            white-space: pre;
            font-family: ${style.fontFamily};
            font-size: ${style.fontSize};
            font-weight: ${style.fontWeight};
            font-style: ${style.fontStyle};
            font-variant: ${style.fontVariant};
            font-stretch: ${style.fontStretch};
            letter-spacing: ${style.letterSpacing};
            word-spacing: ${style.wordSpacing};
            line-height: ${style.lineHeight};
          `;
          document.body.appendChild(measureElement);

          isMeasurementReady = true;
          emojiWidthCache.clear();
          this.updatePreview();

          return;
        })
        .catch(err => {
          // eslint-disable-next-line no-console
          console.error('Error initializing emoji measurement:', err);
        });
    },

    destroyMeasurement() {
      if (measureElement && measureElement.parentNode) {
        document.body.removeChild(measureElement);
        measureElement = null;
      }
      emojiWidthCache.clear();
    },

    /**
     * Detect soft wrap line breaks by measuring character positions in a mirror element.
     * Returns character indices where the textarea would soft-wrap (excludes explicit \n).
     */
    detectLineBreaks(text) {
      if (!this.$refs.textarea || !text) {
        return new Set();
      }

      const charPositions = this.measureCharacterPositions(text);
      return this.findSoftWrapIndices(text, charPositions);
    },

    /**
     * Create a hidden mirror of the textarea and measure each character's vertical position.
     * The mirror has identical styling so text wraps at the same points.
     */
    measureCharacterPositions(text) {
      const textarea = this.$refs.textarea;
      const style = getComputedStyle(textarea);

      const mirror = document.createElement('div');
      mirror.style.cssText = `
        position: absolute;
        visibility: hidden;
        pointer-events: none;
        white-space: pre-wrap;
        word-wrap: break-word;
        overflow-wrap: break-word;
        font-family: ${style.fontFamily};
        font-size: ${style.fontSize};
        font-weight: ${style.fontWeight};
        font-style: ${style.fontStyle};
        font-variant: ${style.fontVariant};
        font-stretch: ${style.fontStretch};
        letter-spacing: ${style.letterSpacing};
        word-spacing: ${style.wordSpacing};
        line-height: ${style.lineHeight};
        padding: ${style.padding};
        border: ${style.border};
        box-sizing: ${style.boxSizing};
        width: ${textarea.offsetWidth}px;
      `;
      document.body.appendChild(mirror);

      // Measure each character with a Range over one text node: per-character
      // spans would break kerning, so the mirror would wrap differently.
      const textNode = document.createTextNode(text);
      mirror.appendChild(textNode);

      const range = document.createRange();
      let offset = 0;
      const positions = [ ...text ].map(char => {
        range.setStart(textNode, offset);
        range.setEnd(textNode, offset + char.length);
        offset += char.length;

        return Math.round(range.getBoundingClientRect().top);
      });

      document.body.removeChild(mirror);

      return positions;
    },

    /**
     * Find indices where soft wraps occur by comparing vertical positions.
     * A soft wrap is when offsetTop increases but it's not due to an explicit newline.
     */
    findSoftWrapIndices(text, positions) {
      const chars = [ ...text ];
      const lineBreakIndices = new Set();
      let lastTop = positions[0] || 0;
      let lastWasNewline = false;

      positions.forEach((currentTop, index) => {
        const isNewline = chars[index] === '\n';

        if (currentTop > lastTop && !isNewline && !lastWasNewline) {
          lineBreakIndices.add(index);
        }

        lastTop = currentTop;
        lastWasNewline = isNewline;
      });

      return lineBreakIndices;
    },

    measureEmojiWidth(emoji) {
      const cached = emojiWidthCache.get(emoji);
      if (cached !== undefined) {
        return cached;
      }

      if (!measureElement || !isMeasurementReady) {
        return 20; // Fallback
      }

      measureElement.textContent = emoji;
      const width = measureElement.getBoundingClientRect().width;

      emojiWidthCache.set(emoji, width);
      return width;
    },

    // Custom scrollbar methods
    updateScrollbar() {
      const textarea = this.$refs.textarea;
      if (!textarea) {
        return;
      }

      const { scrollHeight, clientHeight, scrollTop } = textarea;
      const hasScroll = scrollHeight > clientHeight;

      this.scrollbarVisible = hasScroll;

      if (hasScroll) {
        const trackHeight = clientHeight - 4;
        const ratio = clientHeight / scrollHeight;
        this.thumbHeight = Math.max(ratio * trackHeight, 30);
        const maxThumbTop = trackHeight - this.thumbHeight;
        const scrollRatio = scrollTop / (scrollHeight - clientHeight);
        this.thumbTop = scrollRatio * maxThumbTop;
      }
    },

    onThumbMouseDown(e) {
      e.preventDefault();
      this.isDragging = true;
      this.dragStartY = e.clientY;
      this.dragStartScrollTop = this.$refs.textarea?.scrollTop || 0;
      document.addEventListener('mousemove', this.onThumbMouseMove);
      document.addEventListener('mouseup', this.onThumbMouseUp);
    },

    onThumbMouseMove(e) {
      if (!this.isDragging || !this.$refs.textarea) {
        return;
      }

      const deltaY = e.clientY - this.dragStartY;
      const { scrollHeight, clientHeight } = this.$refs.textarea;
      const trackHeight = clientHeight - 4;
      const maxThumbTop = trackHeight - this.thumbHeight;
      const scrollRatio = deltaY / maxThumbTop;
      const maxScrollTop = scrollHeight - clientHeight;

      this.$refs.textarea.scrollTop = this.dragStartScrollTop + scrollRatio * maxScrollTop;
      this.syncScroll();
    },

    onThumbMouseUp() {
      this.isDragging = false;
      document.removeEventListener('mousemove', this.onThumbMouseMove);
      document.removeEventListener('mouseup', this.onThumbMouseUp);
    },

    onTrackClick(e) {
      const textarea = this.$refs.textarea;
      const scrollTrack = this.$refs.scrollTrack;
      const scrollThumb = this.$refs.scrollThumb;

      if (!textarea || !scrollTrack || e.target === scrollThumb) {
        return;
      }

      const trackRect = scrollTrack.getBoundingClientRect();
      const clickY = e.clientY - trackRect.top;
      const { scrollHeight, clientHeight } = textarea;
      const trackHeight = clientHeight - 4;
      const scrollRatio = clickY / trackHeight;
      const maxScrollTop = scrollHeight - clientHeight;

      textarea.scrollTop = scrollRatio * maxScrollTop;
      this.syncScroll();
    },

    addEmoji(emoji) {
      const textarea = this.$refs.textarea;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;

      textarea.focus();
      textarea.setSelectionRange(start, end);
      document.execCommand('insertText', false, emoji.native);
      this.text = textarea.value;

      this.showEmojiPicker = false;
      this.$nextTick(() => this.updatePreview());
    },

    updatePreview() {
      this.textPreview = this.convertToFacebookEmojis(this.text);
      this.$nextTick(() => this.syncScroll());
    },

    convertToFacebookEmojis(text) {
      if (!text) {
        return '';
      }

      const lineBreakIndices = this.detectLineBreaks(text);

      let result = '';
      let charIndex = 0;

      // Append text with soft wraps and newlines converted to <br/>
      const appendText = function(str) {
        for (const char of str) {
          if (lineBreakIndices.has(charIndex)) {
            result += '<br/>';
          }
          result += char === '\n' ? '<br/>' : (HTML_ESCAPES[char] ?? char);
          ++charIndex;
        }
      };

      let lastIndex = 0;
      let match;

      while ((match = emojiRegex.exec(text)) !== null) {
        appendText(text.substring(lastIndex, match.index));

        if (lineBreakIndices.has(charIndex)) {
          result += '<br/>';
        }

        const emojiChar = match[0];
        const codePoint = [ ...emojiChar ].map(char => char.codePointAt(0).toString(16)).join('-').toUpperCase();
        const emoji = Object.values(emojiData.emojis).find(e => e.b === codePoint || e.c === codePoint);

        if (!emoji) {
          result += emojiChar;
        }
        else {
          const measuredWidth = this.measureEmojiWidth(emojiChar);
          const imgUrl = process.env.VUE_APP_CDN_URL + '/emoji/' + emoji.b.toLowerCase() + '.png';
          result += `<img src="${imgUrl}" class="emoji" style="width:${measuredWidth}px" />`;
        }

        charIndex += [ ...emojiChar ].length;
        lastIndex = match.index + emojiChar.length;
      }

      appendText(text.substring(lastIndex));

      if (result.endsWith('<br/>')) {
        result += '&nbsp;';
      }

      return result;
    },

    syncScroll() {
      const textarea = this.$refs.textarea;
      const overlay = this.$refs.overlay;

      if (textarea && overlay) {
        overlay.scrollTop = textarea.scrollTop;
        overlay.scrollLeft = textarea.scrollLeft;
      }
      this.updateScrollbar();
    },

    /**
     * Prevent more than maxConsecutiveNewlines consecutive newlines in textarea.
     * Handles both typing (Enter key) and pasting.
     */
    setupNewlineRestriction() {
      const textarea = this.$refs.textarea;
      const max = this.maxConsecutiveNewlines;

      const limitNewlines = value => {
        const pattern = new RegExp(`\\n{${max + 1},}`, 'g');
        return value.replace(pattern, '\n'.repeat(max));
      };

      textarea.addEventListener('keydown', e => {
        if (e.key === 'Enter') {
          const { value, selectionStart } = textarea;
          const before = value.substring(0, selectionStart);
          const newlineCount = (before.match(/\n*$/) || [ '' ])[0].length;

          if (newlineCount >= max) {
            e.preventDefault();
          }
        }
      });

      textarea.addEventListener('paste', e => {
        e.preventDefault();
        const pastedText = e.clipboardData.getData('text');
        const limitedText = limitNewlines(pastedText);

        const { selectionStart, selectionEnd } = textarea;
        const before = textarea.value.substring(0, selectionStart);
        const after = textarea.value.substring(selectionEnd);

        // Also limit newlines at the junction points
        let combined = limitNewlines(before + limitedText + after);
        // Native maxlength does not apply to programmatic value assignment, so enforce it here too.
        if (this.maxlength != null) {
          combined = combined.slice(0, this.maxlength);
        }
        textarea.value = combined;
        this.text = combined;

        const newPos = Math.min(selectionStart + limitedText.length, combined.length);
        textarea.setSelectionRange(newPos, newPos);
        this.updatePreview();
      });
    },
  },
};
</script>
